/**
 * useAdminNotifications — polling reutilizable + sonido configurable
 * ----------------------------------------------------------------
 * - Pide /api/admin/notifications/resumen cada N segundos (default 60s)
 * - Si algún contador sube → toca un "ding" sintético (Web Audio API, 0 assets)
 * - Sonido activable/desactivable por admin (localStorage)
 * - Se usa desde cualquier página admin; todas ven el mismo resumen global
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../services/api';

const POLL_MS = 60000; // 60 segundos
const LS_KEY_SOUND = 'cm_admin_notif_sound';

// --- Web Audio API: "ding" sutil, sin archivos externos ---
let _audioCtx = null;
const playDing = () => {
  try {
    if (!_audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      _audioCtx = new Ctx();
    }
    const ctx = _audioCtx;
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;
    // Dos tonos cortos tipo "ding-ding" suaves
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t0 = now + i * 0.12;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.15, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.28);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.3);
    });
  } catch {/* silencio total si no hay AudioContext */}
};

export default function useAdminNotifications({ enabled = true, pollMs = POLL_MS } = {}) {
  const [data, setData] = useState({ total: 0, modulos: {} });
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const v = typeof window !== 'undefined' ? localStorage.getItem(LS_KEY_SOUND) : null;
    return v === null ? true : v === '1';
  });
  const prevTotalRef = useRef(null); // null = primera carga (no toca sonido)
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchOnce = useCallback(async () => {
    try {
      const res = await api.get('/admin/notifications/resumen');
      if (!mountedRef.current) return;
      const nuevo = res.data || { total: 0, modulos: {} };
      setData(nuevo);
      // Solo suena si es una actualización y el contador SUBE
      if (prevTotalRef.current !== null && nuevo.total > prevTotalRef.current && soundEnabled) {
        playDing();
      }
      prevTotalRef.current = nuevo.total;
    } catch {/* silencioso, no spammeamos logs */}
  }, [soundEnabled]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) return () => { mountedRef.current = false; };
    fetchOnce();
    intervalRef.current = setInterval(fetchOnce, pollMs);
    return () => {
      mountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, pollMs, fetchOnce]);

  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem(LS_KEY_SOUND, next ? '1' : '0'); } catch {}
      // Feedback inmediato al activar → un ding de prueba
      if (next) playDing();
      return next;
    });
  }, []);

  const refresh = fetchOnce;

  return {
    total: data.total || 0,
    modulos: data.modulos || {},
    soundEnabled,
    toggleSound,
    refresh,
  };
}
